// src/pages/FacturasTimbradas.js
// Vista de facturas ya timbradas (consulta FacturaFletera con ConTimbre=true).
// Alcance: ver, filtrar y exportar. Sin configuración de columnas ni drag,
// esa maquinaria vive en Bitacoras.js y aquí no hace falta.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useSftfApi } from "../api/sftfClient";
import { getComparator, stableSort } from "../utils/tableSort";
import {
  FACTURAS_TIMBRADAS_COLUMNS,
  FACTURAS_TIMBRADAS_DATE_COLUMN_IDS,
  etiquetaEstatusFactura,
  valorCeldaFacturaTimbrada,
} from "../config/facturasTimbradasColumns";
import {
  exportBitacorasToExcel,
  buildExportFileName,
} from "../utils/exportBitacorasToExcel";

export default function FacturasTimbradas() {
  const { auth } = useAuth();
  const { consultarFacturasTimbradas, consultarEstatusFactura } = useSftfApi(auth);

  const [registros, setRegistros] = useState([]);
  const [catalogoEstatus, setCatalogoEstatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryInfo, setRetryInfo] = useState(null);

  const [filterText, setFilterText] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("FechaFactura");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRetryInfo(null);
    try {
      const [facturas, estatus] = await Promise.all([
        consultarFacturasTimbradas({}, { onRetry: setRetryInfo }),
        consultarEstatusFactura(),
      ]);
      setRegistros(Array.isArray(facturas) ? facturas : []);
      setCatalogoEstatus(Array.isArray(estatus) ? estatus : []);
    } catch (err) {
      setError(err?.message || "No se pudieron cargar las facturas timbradas.");
      setRegistros([]);
    } finally {
      setLoading(false);
      setRetryInfo(null);
    }
  }, [consultarFacturasTimbradas, consultarEstatusFactura]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Volver a la primera página cuando cambia lo que se ve.
  useEffect(() => {
    setPage(0);
  }, [filterText, order, orderBy]);

  const filtrados = useMemo(() => {
    const texto = filterText.trim().toLowerCase();
    if (!texto) return registros;
    return registros.filter((registro) =>
      FACTURAS_TIMBRADAS_COLUMNS.some((col) =>
        String(valorCeldaFacturaTimbrada(registro, col.id, catalogoEstatus))
          .toLowerCase()
          .includes(texto)
      )
    );
  }, [registros, filterText, catalogoEstatus]);

  const ordenados = useMemo(() => {
    // El Estatus se muestra como su etiqueta ("Procesada", "Cancelada"), no
    // como el código numérico crudo. Ordenar por el código haría que la
    // columna se vea ordenada al ojo pero en realidad no lo esté (p.ej.
    // Estatus 4 "Cancelada" antes que Estatus 1 "Procesada"), lo que en una
    // pantalla de conciliación hace desconfiar de la tabla entera.
    if (orderBy === 'Estatus') {
      const conEtiqueta = filtrados.map((registro) => ({
        registro,
        etiqueta: etiquetaEstatusFactura(registro, catalogoEstatus),
      }));
      const comparador = (a, b) => {
        if (a.etiqueta === b.etiqueta) return 0;
        const signo = a.etiqueta > b.etiqueta ? 1 : -1;
        return order === 'asc' ? signo : -signo;
      };
      return stableSort(conEtiqueta, comparador).map((item) => item.registro);
    }
    return stableSort(filtrados, getComparator(order, orderBy));
  }, [filtrados, order, orderBy, catalogoEstatus]);

  const pagina = useMemo(
    () => ordenados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [ordenados, page, rowsPerPage]
  );

  const handleSort = (columnId) => {
    setOrder((prev) => (orderBy === columnId && prev === "asc" ? "desc" : "asc"));
    setOrderBy(columnId);
  };

  const handleExportar = () => {
    // El estatus se exporta ya resuelto a su etiqueta: en el Excel debe leerse
    // "Procesada", no el entero 1.
    const filas = ordenados.map((registro) => ({
      ...registro,
      Estatus: etiquetaEstatusFactura(registro, catalogoEstatus),
    }));

    exportBitacorasToExcel(filas, FACTURAS_TIMBRADAS_COLUMNS, {
      dateColumnIds: FACTURAS_TIMBRADAS_DATE_COLUMN_IDS,
      actionColumnIds: [],
      sheetName: "Facturas timbradas",
      fileName: buildExportFileName(new Date(), "FacturasTimbradas"),
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Facturas timbradas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Bitácoras que ya tienen factura registrada en Cryoinfra, con su UUID,
        serie, folio y totales.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Buscar"
            placeholder="UUID, folio, serie, estatus…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            size="small"
            fullWidth
            disabled={loading}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={cargar}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportar}
            disabled={loading || ordenados.length === 0}
          >
            Exportar a Excel
          </Button>
        </Stack>
      </Paper>

      {loading && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">
            {retryInfo
              ? `Reintentando (${retryInfo.intento} de ${retryInfo.maxIntentos})…`
              : "Consultando facturas timbradas…"}
          </Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && registros.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No se encontraron facturas timbradas.
        </Alert>
      )}

      {!loading && !error && registros.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {`${ordenados.length} facturas`}
            {filterText.trim() ? ` (de ${registros.length} cargadas)` : ""}
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: "65vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {FACTURAS_TIMBRADAS_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{ minWidth: col.minWidth, fontWeight: "bold", whiteSpace: "nowrap" }}
                      sortDirection={orderBy === col.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagina.map((registro, index) => (
                  <TableRow key={`${registro.ClaveFactura ?? "sin-clave"}-${index}`} hover>
                    {FACTURAS_TIMBRADAS_COLUMNS.map((col) => (
                      <TableCell key={col.id} sx={{ whiteSpace: "nowrap" }}>
                        {valorCeldaFacturaTimbrada(registro, col.id, catalogoEstatus)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={ordenados.length}
            page={page}
            onPageChange={(e, nuevaPagina) => setPage(nuevaPagina)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Filas por página"
          />
        </>
      )}
    </Box>
  );
}
